import { localizePath } from "@/lib/locale";
import { baseLocale, getLocale, locales, type Locale } from "@/paraglide/runtime.js";
import { appLanguageOf, DEFAULT_LANGUAGE, type AppLanguage } from "@complete-athlete/enums";

/**
 * The app's own language a post in `locale` is read in: its own when the app speaks it (`/pl/...`),
 * else the default at the plain URL.
 */
export const frameLocaleFor = (locale: AppLanguage): Locale =>
  (locales as readonly string[]).includes(locale) ? (locale as Locale) : baseLocale;

/** Where one language of a post lives, unprefixed or under its own language's prefix. */
export const blogArticlePath = (slug: string, locale: AppLanguage) =>
  localizePath(`/blog/${slug}`, frameLocaleFor(locale));

/** A category's page in one language, at that language's own address for it. */
export const blogCategoryPath = (slug: string, locale: AppLanguage) =>
  localizePath(`/blog/category/${slug}`, frameLocaleFor(locale));

/**
 * The absolute address of one language of a post on the blog that owns it. The API names the
 * owning blog (`canonicalUrl` is the athlete app's copy for a post on both); this keeps its origin
 * and gives the path its language.
 */
export const blogArticleUrl = (ownerUrl: string, slug: string, locale: AppLanguage) =>
  `${new URL(ownerUrl).origin}${blogArticlePath(slug, locale)}`;

/** The language the reader has the app in, as a blog language: posts come in it when they have it. */
export const readerLanguage = (): AppLanguage => appLanguageOf(getLocale()) ?? DEFAULT_LANGUAGE;
